import React, { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';

/**
 * VIDEO SHOWCASE — Two real videos (bg-vid1, bg-vid2) from public/.
 *
 * On click, the selected video expands from its card position to
 * a cinematic full-viewport overlay with GSAP FLIP-style animation.
 * Background blurs and darkens. Close with X or click outside.
 */
export default function VideoShowcase() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardRefs = useRef([]);
  const overlayRef = useRef(null);
  const expandedRef = useRef(null);
  const expandedVideoRef = useRef(null);
  const [focused, setFocused] = useState(null);
  const { t } = useTranslation();

  const VIDEOS = [
    {
      id: 'demo-1',
      title: t('video.0.title'),
      subtitle: t('video.0.subtitle'),
      src: '/bg-vid1.mp4',
    },
    {
      id: 'demo-2',
      title: t('video.1.title'),
      subtitle: t('video.1.subtitle'),
      src: '/bg-vid2.mp4',
    },
  ];

  // Scroll-triggered entry
  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Heading animation
      if (headingRef.current) {
        gsap.fromTo(headingRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1, y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: headingRef.current, start: 'top 85%' },
          }
        );
      }

      // Cards parallax entry
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        gsap.fromTo(card,
          { opacity: 0, y: 80, scale: 0.9, rotateY: i === 0 ? 5 : -5 },
          {
            opacity: 1, y: 0, scale: 1, rotateY: 0,
            duration: 1.2,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              end: 'top 45%',
              scrub: 1,
            },
          }
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Focus / expand
  const openFocus = useCallback((index) => {
    setFocused(index);
    const card = cardRefs.current[index];
    if (!card || !overlayRef.current || !expandedRef.current) return;

    const rect = card.getBoundingClientRect();

    gsap.set(expandedRef.current, {
      position: 'fixed', top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
      borderRadius: '1.5rem', zIndex: 100,
      opacity: 1, visibility: 'visible',
    });

    gsap.to(overlayRef.current, {
      opacity: 1, visibility: 'visible',
      duration: 0.4, ease: 'power2.out',
    });

    const pad = window.innerWidth < 640 ? 16 : 40;
    const maxW = Math.min(window.innerWidth - pad * 2, 1000);
    const maxH = Math.min(window.innerHeight - pad * 2, 700);

    gsap.to(expandedRef.current, {
      top: (window.innerHeight - maxH) / 2,
      left: (window.innerWidth - maxW) / 2,
      width: maxW, height: maxH,
      borderRadius: '1.5rem',
      duration: 0.7, ease: 'power3.inOut',
      onComplete: () => {
        if (expandedVideoRef.current) {
          expandedVideoRef.current.play();
        }
      },
    });

    document.body.style.overflow = 'hidden';
  }, []);

  const closeFocus = useCallback(() => {
    if (focused === null) return;
    const card = cardRefs.current[focused];
    if (!card || !overlayRef.current || !expandedRef.current) {
      setFocused(null); return;
    }

    if (expandedVideoRef.current) expandedVideoRef.current.pause();

    const rect = card.getBoundingClientRect();
    gsap.to(expandedRef.current, {
      top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
      duration: 0.6, ease: 'power3.inOut',
      onComplete: () => {
        gsap.set(expandedRef.current, { visibility: 'hidden', opacity: 0 });
        setFocused(null);
      },
    });

    gsap.to(overlayRef.current, {
      opacity: 0, duration: 0.4, ease: 'power2.in',
      onComplete: () => gsap.set(overlayRef.current, { visibility: 'hidden' }),
    });

    document.body.style.overflow = '';
  }, [focused]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') closeFocus(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [closeFocus]);

  const current = focused !== null ? VIDEOS[focused] : null;

  return (
    <section ref={sectionRef} id="video" className="relative py-32 lg:py-40 bg-linx-surface-2">
      <div className="section-container">
        {/* Header */}
        <div className="mb-16 lg:mb-20 text-center">
          <span className="inline-block font-mono text-xs text-secondary-dark tracking-[0.2em] uppercase mb-4 opacity-80">
            {t('video.label')}
          </span>
          <h2
            ref={headingRef}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary"
            aria-label={t('video.heading')}
          >
            {t('video.heading')}
          </h2>
        </div>

        {/* Video cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {VIDEOS.map((video, i) => (
            <button
              key={video.id}
              ref={(el) => (cardRefs.current[i] = el)}
              onClick={() => openFocus(i)}
              className="group relative aspect-video rounded-3xl overflow-hidden cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue"
              aria-label={`Voir: ${video.title}`}
            >
              {/* Video thumbnail (auto plays muted) */}
              <video
                autoPlay muted loop playsInline
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              >
                <source src={video.src} type="video/mp4" />
              </video>

              {/* Overlay */}
              className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/20 to-transparent group-hover:from-primary/50 transition-all duration-500" />

              {/* Play icon */}
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-white/20 transition-all duration-500">
                  <svg className="w-7 h-7 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>

              {/* Label */}
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <h3 className="font-display text-xl font-bold text-white mb-1">{video.title}</h3>
                <p className="text-sm text-white/50">{video.subtitle}</p>
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-3xl border border-primary/[0.06] group-hover:border-primary/[0.12] transition-colors duration-500 pointer-events-none" />
            </button>
          ))}
        </div>
      </div>

      {/* Overlay */}
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[90] cursor-pointer"
        style={{ opacity: 0, visibility: 'hidden' }}
        onClick={closeFocus}
      />

      {/* Expanded video */}
      <div
        ref={expandedRef}
        className="bg-black rounded-3xl overflow-hidden"
        style={{ opacity: 0, visibility: 'hidden', position: 'fixed', zIndex: 100 }}
        role="dialog"
        aria-modal={focused !== null}
        aria-label={current?.title || 'Video'}
      >
        {focused !== null && (
          <div className="relative w-full h-full">
            <button
              onClick={closeFocus}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
              aria-label="Fermer"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <video
              ref={expandedVideoRef}
              controls
              autoPlay
              className="w-full h-full object-cover"
            >
              <source src={current?.src} type="video/mp4" />
            </video>
          </div>
        )}
      </div>
    </section>
  );
}
